import { Suspense } from "react"
import { makeStyles } from "tss-react/mui"
import { Grid, Tooltip, Typography } from "@mui/material"
import { DashboardCardSkeleton } from "../../../../dashboards/components/DashboardCardSkeleton"
import { QueryErrorBoundary, QueryErrorFallbackProps } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { MerchantHomeQuery, fetchTodaysControlCode } from "../merchantHomeApi"
import { CardUiParams } from "../../../../dashboards/components/DashboardCard"

const useStyles = makeStyles()((theme) => ({
    container: {
        color: theme.palette.tailwind.slate[700],
        background: theme.palette.tailwind.sky[200],
        borderRadius: "0.5rem",
        textAlign: "center",
        maxWidth: CardUiParams.WIDTH,
        padding: "1.5rem 1rem",
        [theme.breakpoints.down("xl")]: {
            width: CardUiParams.WIDTH_SM,
        },
    },
    errorText: {
        color: theme.palette.tailwind.slate[500],
    },
}))

function isValidControlCode(code: string | number | undefined) {
    if (code === undefined) return false
    if (typeof code === "number") return true

    return !Number.isNaN(Number(code))
}

export function ControlCodeContent() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()

    const todaysControlCode = useApiQuery(fetchTodaysControlCode, {
        queryName: MerchantHomeQuery.TODAYS_CONTROL_CODE,
        dependencies: {
            merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return (
        <LocalizedStrict id="merchant-home-todays-control-code-tooltip" attrs={{ title: true }}>
            <Tooltip
                arrow
                title="This will be displayed on the proof of purchase (app) and the printed receipt"
                placement="top"
            >
                <Grid container flexDirection="column" gap={2} className={classes.container}>
                    <>
                        <LocalizedStrict id="merchant-home-todays-control-code-title">
                            <Typography variant="h6">Todays control code</Typography>
                        </LocalizedStrict>
                        {isValidControlCode(todaysControlCode.data?.receipt.controlCode) ? (
                            <Typography variant="h3" fontWeight={500}>
                                {todaysControlCode.data?.receipt.controlCode || 0}
                            </Typography>
                        ) : (
                            <LocalizedStrict id="control-kitchen-control-code-not-ready">
                                <Typography variant="h5" fontWeight={500}>
                                    Not ready
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </>
                </Grid>
            </Tooltip>
        </LocalizedStrict>
    )
}

export interface CardErrorFallbackProps {
    title: string
    containerClassName: string
    errorTextClassName: string
}

export function CardErrorFallback({ title, containerClassName, errorTextClassName }: CardErrorFallbackProps) {
    return (
        <Grid container flexDirection="column" gap={2} className={containerClassName}>
            <Typography variant="h6">{title}</Typography>
            <LocalizedStrict id="query-error-boundary-message">
                <Typography variant="body2" className={errorTextClassName}>
                    Could not fetch data
                </Typography>
            </LocalizedStrict>
        </Grid>
    )
}

function ControlCodeErrorFallback(_props: QueryErrorFallbackProps) {
    const { classes } = useStyles()

    return (
        <LocalizedStrict id="merchant-home-todays-control-code" attrs={{ title: true }}>
            <CardErrorFallback
                title="Todays control code"
                containerClassName={classes.container}
                errorTextClassName={classes.errorText}
            />
        </LocalizedStrict>
    )
}

export function ControlCodeCard() {
    return (
        <QueryErrorBoundary Fallback={ControlCodeErrorFallback}>
            <Suspense
                fallback={
                    <>
                        <DashboardCardSkeleton isSmall />
                    </>
                }
            >
                <ControlCodeContent />
            </Suspense>
        </QueryErrorBoundary>
    )
}
